/**
 * Read-only data access for the Saoirse Console substrate views (Phase 1).
 *
 * All reads go through the three pre-existing SECURITY DEFINER RPCs on the
 * main project (signatures + return shapes verified live 2026-06-10):
 *
 *  - list_all_substrate_for_editor()
 *      → id, title, kind, city, latitude, longitude, salience
 *  - list_substrate_for_editor(p_city text)
 *      → id, title, kind, latitude, longitude, salience
 *  - list_providers_for_editor(p_min_lat, p_max_lat, p_min_lng, p_max_lng)
 *      → active providers inside the bbox (8 columns)
 *
 * The city index is DERIVED from data — counts, centroids and bounds are
 * computed client-side from the all-cities call (design 2026-06-10 §2:
 * "No hardcoded city table.").
 *
 * PAGINATION IS LOAD-BEARING: the project's PostgREST max-rows setting caps
 * every response at 1,000 rows no matter what limit/offset/Range asks for
 * (verified live 2026-06-10: limit=100000 and Range:0-99999 both return
 * exactly 1,000 of the 1,482 rows — which means the retired
 * substrate-map-all.html was silently showing only 20 of 28 cities). All
 * reads here therefore page with count=exact until the full set arrives.
 *
 * This module contains NO writes by design: Phase 1 is read-only. Phase 2
 * adds RLS-gated writes only after the held landmarks migration is
 * operator-approved (design §6).
 */
import { supabase } from './supabase';

export type SubstrateRow = {
  id: string;
  title: string;
  kind: string;
  latitude: number;
  longitude: number;
  salience: number;
};

export type SubstrateCityRow = SubstrateRow & { city: string | null };

export type ProviderRow = {
  id: string;
  business_name: string;
  business_type: string | null;
  latitude: number;
  longitude: number;
  saoirse_weight: number | null;
  saoirse_recommended: boolean | null;
  saoirse_excluded: boolean | null;
};

export type Bbox = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

/**
 * Page size per request. The server's max-rows (observed 1,000) silently
 * truncates anything larger, so we never trust a single response: we follow
 * the count=exact total until we hold every row, and treat an empty page as
 * a defensive stop if the count is ever missing.
 */
const PAGE_SIZE = 1000;

async function rpcPagedRows<T>(fn: string, args?: Record<string, unknown>): Promise<T[]> {
  const rows: T[] = [];
  let total: number | null = null;
  for (;;) {
    const from = rows.length;
    const { data, error, count } = await supabase
      .rpc(fn, args, { count: 'exact' })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(`${fn} failed: ${error.message}`);
    const page = (data ?? []) as T[];
    rows.push(...page);
    if (typeof count === 'number') total = count;
    if (total !== null && rows.length >= total) return rows;
    if (page.length === 0) return rows;
  }
}

export async function fetchAllSubstrate(): Promise<SubstrateCityRow[]> {
  return rpcPagedRows<SubstrateCityRow>('list_all_substrate_for_editor');
}

export async function fetchCitySubstrate(city: string): Promise<SubstrateRow[]> {
  return rpcPagedRows<SubstrateRow>('list_substrate_for_editor', { p_city: city });
}

export async function fetchProvidersInBbox(bbox: Bbox): Promise<ProviderRow[]> {
  return rpcPagedRows<ProviderRow>('list_providers_for_editor', {
    p_min_lat: bbox.minLat,
    p_max_lat: bbox.maxLat,
    p_min_lng: bbox.minLng,
    p_max_lng: bbox.maxLng,
  });
}

/** Synthetic bucket name for rows whose city is null/empty (none live today). */
export const NO_CITY = '(no city)';

export type CityIndexEntry = {
  /** City name as stored in the DB, or NO_CITY for the null/empty bucket. */
  name: string;
  count: number;
  /** [lng, lat] mean of member coordinates — aims the camera, nothing more. */
  centroid: [number, number];
  /** [[west, south], [east, north]] extent of member coordinates. */
  bounds: [[number, number], [number, number]];
  /** True for the synthetic NO_CITY bucket (not fetchable by p_city). */
  isNoCityBucket: boolean;
};

type CityAccumulator = {
  count: number;
  sumLng: number;
  sumLat: number;
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
};

/**
 * Group all-cities rows into a sorted city index with counts, centroids and
 * bounds. Rows with non-finite coordinates are skipped; rows with null/empty
 * city land in the NO_CITY bucket so bad data stays visible instead of
 * disappearing.
 */
export function buildCityIndex(rows: ReadonlyArray<SubstrateCityRow>): CityIndexEntry[] {
  const acc = new Map<string, CityAccumulator>();
  for (const row of rows) {
    const lng = Number(row.longitude);
    const lat = Number(row.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    const name = row.city && row.city.trim() !== '' ? row.city : NO_CITY;
    const a = acc.get(name);
    if (!a) {
      acc.set(name, {
        count: 1,
        sumLng: lng,
        sumLat: lat,
        minLng: lng,
        minLat: lat,
        maxLng: lng,
        maxLat: lat,
      });
    } else {
      a.count += 1;
      a.sumLng += lng;
      a.sumLat += lat;
      a.minLng = Math.min(a.minLng, lng);
      a.minLat = Math.min(a.minLat, lat);
      a.maxLng = Math.max(a.maxLng, lng);
      a.maxLat = Math.max(a.maxLat, lat);
    }
  }
  return [...acc.entries()]
    .map(([name, a]): CityIndexEntry => ({
      name,
      count: a.count,
      centroid: [a.sumLng / a.count, a.sumLat / a.count],
      bounds: [
        [a.minLng, a.minLat],
        [a.maxLng, a.maxLat],
      ],
      isNoCityBucket: name === NO_CITY,
    }))
    .sort((x, y) => x.name.localeCompare(y.name));
}

/**
 * Provider-fetch bbox from a city's substrate extent, padded so providers on
 * the periphery still appear (replaces the old pages' hand-tuned per-city
 * bboxes). Pads each axis by 25% of its span, with a 0.02° floor (~2 km).
 */
export function cityProviderBbox(entry: CityIndexEntry): Bbox {
  const [[west, south], [east, north]] = entry.bounds;
  const padLng = Math.max(0.02, (east - west) * 0.25);
  const padLat = Math.max(0.02, (north - south) * 0.25);
  return {
    minLat: south - padLat,
    maxLat: north + padLat,
    minLng: west - padLng,
    maxLng: east + padLng,
  };
}
